import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL } from '@divvy-web/skylab.basicbutton'
import Icon, { ICON_SIZE_EXTRA_LARGE } from '@divvy-web/skylab.icon'
import Image from '@divvy-web/skylab.image'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import Divider from '@sputnik/components/Divider'
import { PRODUCT_CONST_STRINGS, URL } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

const NoUWOfferPage = () => {
  const [getClassName] = useNameFormatter('NoUWOfferPage')
  const { formatMessage } = useIntl()
  const { BILL_SPEND_AND_EXPENSE, BILL, BILL_DIVVY_CARD } = PRODUCT_CONST_STRINGS
  const { BILL_AP_AR } = URL
  const { pathname } = useLocation()

  /* tracking start */

  const { track } = useWebTracking()

  useEffect(() => {
    track('se_signup__no_offer__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  const onGetStartedClick = () => {
    track('se_signup__failure_get_started__triggered', { get_started_window_name: pathname })
    track('se_signup__application_no_uw_offer_xsell_get_started__fired')
    logInfo('onNoOfferGetStartedClick', {
      attributes: {
        result: `Sending user to path: ${BILL_AP_AR}`,
      },
      eventName: 'onNoOfferGetStartedClickNavigate',
    })
    window.open(BILL_AP_AR, '_blank', 'noopener, noreferrer')
  }

  return (
    <>
      <BillBrandLogo />
      <div className={styles[getClassName('')]}>
        <div className={styles[getClassName('header-content')]}>
          <div className={styles[getClassName('heading')]}>
            <FormattedMessage
              defaultMessage='We are unable to offer you a credit line at this time'
              id='sputnik.NoUWOfferPage__D3nyb1'
            />
          </div>
          <div className={styles[getClassName('subtitle')]}>
            <FormattedMessage
              defaultMessage='Based on your responses, {BILL_SPEND_AND_EXPENSE} isn’t available for your business right now'
              id='sputnik.NoUWOfferPage__of2C+6'
              values={{
                BILL_SPEND_AND_EXPENSE: BILL_SPEND_AND_EXPENSE,
              }}
            />
          </div>
        </div>
        <Divider />
        <div className={styles[getClassName('body-container')]}>
          <div className={styles[getClassName('left-column')]}>
            <div className={styles[getClassName('title')]}>
              <FormattedMessage
                defaultMessage='But we can still help you stay in control of your finances'
                id='sputnik.NoUWOfferPage__S4MQqp'
              />
            </div>
            <div className={styles[getClassName('text')]}>
              <FormattedMessage
                defaultMessage='{BILL} for Accounts Payable and Receivable offers a smarter way to simplify your financial operations and improve cash flow.'
                id='sputnik.NoUWOfferPage__GEvxOX'
                values={{
                  BILL: BILL,
                }}
              />
            </div>
            <div className={styles[getClassName('list')]}>
              <div className={styles[getClassName('icon-text')]}>
                <div>
                  <Icon
                    name='arrow-right'
                    size={ICON_SIZE_EXTRA_LARGE}
                  />
                </div>
                <div>
                  <div className={styles[getClassName('icon-text-header')]}>
                    <FormattedMessage
                      defaultMessage='Delay payments with Pay By Card'
                      id='sputnik.NoUWOfferPage__JjcRu1'
                    />
                  </div>
                  <div className={styles[getClassName('icon-text-subtext')]}>
                    <FormattedMessage
                      defaultMessage='Use your credit card to pay vendors, even if they don’t accept cards.'
                      id='sputnik.NoUWOfferPage__zdwXJb'
                    />
                  </div>
                </div>
              </div>
              <div className={styles[getClassName('icon-text')]}>
                <div>
                  <Icon
                    name='arrow-right'
                    size={ICON_SIZE_EXTRA_LARGE}
                  />
                </div>
                <div>
                  <div className={styles[getClassName('icon-text-header')]}>
                    <FormattedMessage
                      defaultMessage='Accelerate your incoming cash'
                      id='sputnik.NoUWOfferPage__zRgBKv'
                    />
                  </div>
                  <div className={styles[getClassName('icon-text-subtext')]}>
                    <FormattedMessage
                      defaultMessage='With invoice financing and access customer payments up to 60 days early.'
                      id='sputnik.NoUWOfferPage__KuVWnQ'
                    />
                  </div>
                </div>
              </div>
              <div className={styles[getClassName('icon-text')]}>
                <div>
                  <Icon
                    name='arrow-right'
                    size={ICON_SIZE_EXTRA_LARGE}
                  />
                </div>
                <div>
                  <div className={styles[getClassName('icon-text-header')]}>
                    <FormattedMessage
                      defaultMessage='Get real-time visibility'
                      id='sputnik.NoUWOfferPage__1D1WB9'
                    />
                  </div>
                  <div className={styles[getClassName('icon-text-subtext')]}>
                    <FormattedMessage
                      defaultMessage='With a platform that syncs directly with your accounting software to stay up to date.'
                      id='sputnik.NoUWOfferPage__osHnd9'
                    />
                  </div>
                </div>
              </div>
              <div className={styles[getClassName('get-started-button')]}>
                <BasicButton
                  size={BASIC_BUTTON_SIZE_SMALL}
                  onClick={onGetStartedClick}
                >
                  <FormattedMessage
                    defaultMessage='Get started'
                    id='sputnik.NoUWOfferPage__/aBLH2'
                  />
                </BasicButton>
              </div>
            </div>
          </div>
          <div>
            <Image
              alt={formatMessage({
                defaultMessage: 'woman looking at her phone with a phone notification that says "payment submitted"',
                id: 'sputnik.NoUWOfferPage__a5N3Qu',
              })}
              className={styles[getClassName('image')]}
              src='https://app.divvy.co/assets/stock-photos/credit-application-no-offer-page.png'
            />
          </div>
        </div>
        <div className={styles[getClassName('footer')]}>
          <FormattedMessage
            defaultMessage='The {BILL_DIVVY_CARD} may be issued by WebBank Salt Lake City, UT, and Cross River Bank Fort Lee, NJ, member FDIC'
            id='sputnik.NoUWOfferPage__1b2d6+'
            values={{
              BILL_DIVVY_CARD: BILL_DIVVY_CARD,
            }}
          />
        </div>
      </div>
    </>
  )
}

export default NoUWOfferPage
